import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../../services/rptService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Calculate as CalculateIcon,
  MonetizationOn as MonetizationOnIcon,
  Home as HomeIcon,
  EventNote as EventNoteIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

const TaxCalculator = () => {
  const navigate = useNavigate();
  const [assessedValue, setAssessedValue] = useState('');
  const [yearFrom, setYearFrom] = useState(new Date().getFullYear() - 5);
  const [yearTo, setYearTo] = useState(new Date().getFullYear());
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculatedTax, setCalculatedTax] = useState(null);
  const [yearBreakdown, setYearBreakdown] = useState([]);

  const handleCalculate = async () => {
    const av = parseFloat(assessedValue);
    if (!av || av <= 0) return;
    if (!yearFrom || !yearTo || yearFrom > yearTo) return;
    setIsCalculating(true);
    try {
      const amnestyResult = await rptService.getAllAmnesty();
      const amnestyRecords = amnestyResult.success ? (amnestyResult.data || []) : [];
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const breakdown = [];
      let totalTax = 0;
      for (let year = yearFrom; year <= yearTo; year++) {
        const taxDue = av * 0.01;
        const fullPayment = taxDue * 2;
        let discount = 0;
        let discountPercent = 0;
        let penalty = 0;
        let penaltyPercent = 0;
        let status = '';
        let amnestyApplied = false;
        const applicableAmnesty = amnestyRecords.find(a => year >= a.tax_year_from && year <= a.tax_year_to);
        if (applicableAmnesty) {
          penaltyPercent = applicableAmnesty.penalty_percent;
          penalty = fullPayment * (penaltyPercent / 100);
          status = penaltyPercent === 0 ? 'Amnesty' : `Penalty (${penaltyPercent}% penalty)`;
          amnestyApplied = penaltyPercent === 0;
        } else {
          if (year > currentYear) {
            discountPercent = 20;
            discount = fullPayment * 0.20;
            status = 'Advance Payment (20% discount)';
          } else if (year === currentYear) {
            if (currentMonth >= 1 && currentMonth <= 3) {
              discountPercent = 10;
              discount = fullPayment * 0.10;
              status = 'Early Payment (10% discount)';
            } else if (currentMonth >= 4) {
              const monthsSinceApril = currentMonth - 4 + 1;
              penaltyPercent = Math.min(8 + (monthsSinceApril - 1) * 2, 72);
              penalty = fullPayment * (penaltyPercent / 100);
              status = `Penalty (${penaltyPercent}%)`;
            }
          } else {
            const yearsPast = currentYear - year;
            let monthsFromTaxYear = 9;
            let monthsFromCompleteYears = (yearsPast - 1) * 12;
            let monthsInCurrentYear = currentMonth;
            const totalPenaltyMonths = monthsFromTaxYear + monthsFromCompleteYears + monthsInCurrentYear;
            penaltyPercent = Math.min(8 + (totalPenaltyMonths - 1) * 2, 72);
            penalty = fullPayment * (penaltyPercent / 100);
            status = `Overdue (${penaltyPercent}% penalty)`;
          }
        }
        const total = fullPayment - discount + penalty;
        totalTax += total;
        breakdown.push({
          year,
          assessedValue: av,
          taxDue,
          fullPayment,
          discount,
          discountPercent,
          penalty,
          penaltyPercent,
          status,
          total,
          amnestyApplied
        });
      }
      setYearBreakdown(breakdown);
      setCalculatedTax(totalTax);
    } finally {
      setIsCalculating(false);
    }
  };

  const toCurrency = (n) => `₱${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/portal/property-tax/dashboard')}
            >
              RPT System
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>Tax Calculator</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 800, 
            color: '#1e293b', 
            letterSpacing: '-0.02em',
            background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <CalculateIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Tax Calculator
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', lineHeight: 1.6 }}>
            Official real property tax computation tool. Calculate tax dues, discounts, and penalties based on assessed value and effectivity years.
            Includes automatic detection of amnesty periods and updated tax schedules.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ mr: 1, color: '#64748b' }} />
              Assessment Details
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  label="Assessed Value"
                  type="number"
                  value={assessedValue}
                  onChange={(e) => setAssessedValue(e.target.value)}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  label="Year From"
                  type="number"
                  value={yearFrom}
                  onChange={(e) => setYearFrom(parseInt(e.target.value) || new Date().getFullYear())}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  label="Year To"
                  type="number"
                  value={yearTo}
                  onChange={(e) => setYearTo(parseInt(e.target.value) || new Date().getFullYear())}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth={{ xs: true, sm: false }}
                    onClick={handleCalculate}
                    disabled={isCalculating}
                    startIcon={<CalculateIcon />}
                    sx={{ 
                      bgcolor: '#1e293b', 
                      '&:hover': { bgcolor: '#334155' },
                      px: 4,
                      py: 1.5,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
                    }}
                  >
                    Calculate Tax
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {calculatedTax !== null && (
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ mb: 4 }} />
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center' }}>
                  <EventNoteIcon sx={{ mr: 1, color: '#64748b' }} />
                  Calculation Results
                </Typography>
                
                <Card sx={{ 
                  bgcolor: '#f0fdf4', 
                  border: '1px solid #bbf7d0', 
                  borderRadius: 2,
                  mb: 4,
                  boxShadow: 'none'
                }}>
                  <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', py: 3, gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="success.dark" fontWeight="600" sx={{ mb: 0.5 }}>
                        TOTAL AMOUNT DUE ({yearFrom} - {yearTo})
                      </Typography>
                      <Typography variant="h3" fontWeight="800" color="#15803d">
                        {toCurrency(calculatedTax)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                         <Chip label={`${yearTo - yearFrom + 1} Years`} size="small" color="success" variant="outlined" sx={{ mr: 1, height: 20, fontSize: '0.7rem' }} />
                         Based on 1% Basic + 1% SEF
                      </Typography>
                    </Box>
                    <MonetizationOnIcon sx={{ fontSize: 60, opacity: 0.2, color: '#15803d', display: { xs: 'none', sm: 'block' } }} />
                  </CardContent>
                </Card>
              </Box>
            )}


            {yearBreakdown.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>Tax Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Tax Due</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Full Payment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Discount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Penalty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {yearBreakdown.map((item, index) => (
                      <TableRow 
                        key={item.year} 
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                          '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                          '&:hover': { bgcolor: '#f1f5f9 !important' },
                          transition: 'background-color 0.1s'
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{item.year}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{toCurrency(item.taxDue)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{toCurrency(item.fullPayment)}</TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ fontFamily: 'monospace', color: item.discount > 0 ? '#16a34a' : 'text.disabled', fontWeight: item.discount > 0 ? 600 : 400 }}
                        >
                          {item.discount > 0 ? `(${toCurrency(item.discount)})` : '-'}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ fontFamily: 'monospace', color: item.penalty > 0 ? '#dc2626' : 'text.disabled', fontWeight: item.penalty > 0 ? 600 : 400 }}
                        >
                          {item.penalty > 0 ? toCurrency(item.penalty) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>
                          {toCurrency(item.total)}
                        </TableCell>
                        <TableCell>
                          {item.status.includes('Amnesty') ? (
                            <Chip label="Amnesty" color="warning" size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
                          ) : item.status.includes('Advance') || item.status.includes('Early') ? (
                            <Chip label="Discounted" color="success" size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
                          ) : item.status.includes('Overdue') || item.status.includes('Penalty') ? (
                            <Chip label="Penalty" color="error" size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
                          ) : (
                            <Typography variant="caption" color="text.secondary">{item.status}</Typography>
                          )}
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                            {item.status}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 8, borderTop: '1px solid #e2e8f0', pt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Tax Calculator
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Figures generated are estimates based on current parameters. Official assessment may vary.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TaxCalculator;
